import { createContext, useContext, useEffect, useState, useCallback, useMemo, type ReactNode } from 'react';
import Lenis from 'lenis';
import { useLocation } from 'react-router-dom';
import { isHomePath } from './MotionProvider';
import { useReducedMotion } from '../../hooks/useReducedMotion';

interface SmoothScrollContextValue {
  lenis: Lenis | null;
  scrollTo: (target: string | number | HTMLElement, options?: ScrollToOptions) => void;
  stop: () => void;
  start: () => void;
  isSmooth: boolean;
}

interface ScrollToOptions {
  offset?: number;
  duration?: number;
  immediate?: boolean;
  lock?: boolean;
}

const SMOOTH_SCROLL_ENABLED = true;

const SmoothScrollContext = createContext<SmoothScrollContextValue>({
  lenis: null,
  scrollTo: () => {},
  stop: () => {},
  start: () => {},
  isSmooth: false,
});

interface SmoothScrollProviderProps {
  children: ReactNode;
}

export function SmoothScrollProvider({ children }: SmoothScrollProviderProps) {
  const [lenis, setLenis] = useState<Lenis | null>(null);
  const prefersReducedMotion = useReducedMotion();
  const { pathname } = useLocation();
  // Lenis smooth-scrolling runs on the home page only (motion policy, 2026-09);
  // every other page uses native scrolling.
  const isSmooth = SMOOTH_SCROLL_ENABLED && !prefersReducedMotion && isHomePath(pathname);

  useEffect(() => {
    if (!isSmooth) {
      setLenis(null);
      return;
    }

    // Optimized Lenis config for smooth but responsive scrolling
    const lenisInstance = new Lenis({
      duration: 0.8, // Reduced from 1.2 for snappier feel
      easing: (t: number) => 1 - Math.pow(1 - t, 3), // Cubic ease-out (faster)
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      touchMultiplier: 1.5, // Reduced for better control
      infinite: false,
      wheelMultiplier: 1, // Default wheel sensitivity
    });

    let rafId: number;
    function raf(time: number) {
      lenisInstance.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);

    setLenis(lenisInstance);

    return () => {
      cancelAnimationFrame(rafId);
      lenisInstance.destroy();
    };
  }, [isSmooth]);

  const scrollTo = useCallback(
    (target: string | number | HTMLElement, options: ScrollToOptions = {}) => {
      if (lenis) {
        lenis.scrollTo(target, {
          offset: options.offset ?? 0,
          duration: options.duration ?? 0.8,
          immediate: options.immediate ?? false,
          lock: options.lock ?? false,
        });
      } else {
        if (typeof target === 'number') {
          window.scrollTo({ top: target, behavior: 'auto' });
        } else if (typeof target === 'string') {
          const element = document.querySelector(target);
          element?.scrollIntoView({ behavior: 'auto' });
        } else if (target instanceof HTMLElement) {
          target.scrollIntoView({ behavior: 'auto' });
        }
      }
    },
    [lenis]
  );

  const stop = useCallback(() => lenis?.stop(), [lenis]);
  const start = useCallback(() => lenis?.start(), [lenis]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({ lenis, scrollTo, stop, start, isSmooth }),
    [lenis, scrollTo, stop, start, isSmooth]
  );

  return (
    <SmoothScrollContext.Provider value={contextValue}>
      {children}
    </SmoothScrollContext.Provider>
  );
}

export function useSmoothScroll() {
  const context = useContext(SmoothScrollContext);
  if (context === undefined) {
    throw new Error('useSmoothScroll must be used within a SmoothScrollProvider');
  }
  return context;
}

export default SmoothScrollProvider;
